import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Constants from './Constants/checkoutConstants';
import { MINI_APP_CONSTANTS } from '@kfc-global/react-shared/constants';

import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import GoIcon from '../../styles/staticAssets/images/GoIcon.png';
import ProceedToPayment from './proceedToPayment';

import PaymentFormCreditCardBilldesk from './PaymentFormCreditCardBilldesk';
import PaymentFormNetBankingOtherWalletUPIBilldesk from './PaymentFormNetBankingOtherWalletUPIBilldesk';
import { GuestContactDetailsClick } from 'common/utilities/analyticsUtils';
import PaymentFormPayTm from './PaymentFormPayTm';
import PaymentFormPhonePe from './PaymentFormPhonePe';
import {
  addToPaymentClickHandler,
  dispatchCreateOrderAction,
  dispatchPayUsingNetBankings,
  dispatchPayUsingPaytm,
  dispatchGiftCardAction,
  dispatchPayUsingPhonePe,
  dispatchPayUsingPhonepeSwitch,
} from './PaymentDispatchFn';
import { updatePaymentAnalytics } from './CheckoutFn';
import { ChannelManager } from 'common/manager';
import {
  resetGoogleSpotPayments,
  googleSpotCheckTransactionStatus,
  googleSpotInitiatePayment,
  googleSpotValidateResponse,
} from '@kfc-global/react-shared/redux/Actions/PaymentGoogleSpotActions';
import {
  checkPhonepeSwitchPaymentStatus,
  resetMiniAppPaymentReducer,
} from '@kfc-global/react-shared/redux/Actions/MicroAppPaymentActions';
import { payUsingPhonePeSDK } from 'common/utilities/phonepeSwitchAppUtils';
import { redirectToPaymentFailurePage, redirectToPaymentSuccessPage } from 'common/utilities/utils';
import { PAYMENT_TENDER_TYPES, ROUTE_URL } from '../../common/constants/SharedConstants';
import { ButtonComp, ButtonType, ContainedButton } from 'atoms/Buttons';
import { onClickContinuePayment } from './CheckoutUtils/CheckoutUtils';

export const renderPaymentForms = params => {
  const { tender, formPayload } = params;
  const { name: tenderName } = tender;
  switch (tenderName) {
    case PAYMENT_TENDER_TYPES.CREDIT_CARD:
      return <PaymentFormCreditCardBilldesk {...formPayload} />;
    case PAYMENT_TENDER_TYPES.NET_BANKING:
      return <PaymentFormNetBankingOtherWalletUPIBilldesk {...formPayload} />;
    // no default
  }
};

export default function Payment({
  productMapById,
  cartData,
  isValidated,
  tenantId,
  userEmail,
  userPhone,
  createOrderRequest,
  subscription,
  checkoutProps,
  paymentEnabled,
  setIsPaymentTypeSelected,
  isClickContinuePayment,
  setIsClickContinuePayment,
  currencyConversionFactor,
  callBackValidateUserDetails,
  tenderInfo,
  paymentFormPayload,
  callBackPaymentErrorHandler,
}) {
  const dispatch = useDispatch();

  const [tender, setTender] = useState(tenderInfo ? tenderInfo : { name: '', payload: {} });

  const [formPayload, setFormPayload] = useState(paymentFormPayload ? paymentFormPayload : {});

  const [isClickAddPayment, setIsClickAddPayment] = useState(false);

  const [isValid, setIsValid] = useState(false);
  const [isGpayPaymentValid, setGpayPaymentValidity] = useState(false);

  const basketId = cartData?.id;

  const { paymentsReducer, profileReducer, paymentsGiftCardReducer, orderReducer } = useSelector(data => {
    return data;
  });

  let userParams = useSelector(data => data?.appStateReducer?.userStatus);

  const {
    initiatePaymentStatus,
    initiatePaymentResponse,
    validatePaymentStatus,
    validatePaymentResponse,
    transactionStatus,
    transactionStatusResponse,
  } = useSelector(data => data?.paymentsGoogleSpotReducer || {});

  const {
    response: microAppPaymentResponse = {},
    isPaymentCompleted = false,
    phonepeStatusResponse = {},
  } = useSelector(state => state.microAppPaymentReducer);
  const { paymentStatusMaxCallCount, componentOptions } = useSelector(data => data.tenantReducer);

  const { CHECKOUT_TITLES, ADD_PAY_METHOD, PHONEPE_SWITCH_CONSTANTS } = Constants;

  useEffect(() => {
    dispatch(resetMiniAppPaymentReducer());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ChannelManager.isGoogle && cartData?.allowedTenders?.includes(ChannelManager.channel)) {
      setTender({ name: ChannelManager.channel });
      setIsPaymentTypeSelected(true);
    } else if (ChannelManager.isPhonePe && cartData?.allowedTenders?.includes(PAYMENT_TENDER_TYPES.PHONEPE_SWITCH)) {
      setTender({ name: PAYMENT_TENDER_TYPES.PHONEPE_SWITCH });
      setIsPaymentTypeSelected(true);
    }
  }, [cartData?.allowedTenders, setIsPaymentTypeSelected]);

  useEffect(() => {
    let paymentObject = {
      isClickContinuePayment,
      subscription,
      isLoggedIn: userParams?.loggedIn,
      GuestContactDetailsClick,
      updatePaymentAnalytics,
      cartData,
      currencyConversionFactor,
      tender,
      productMapById,
      setIsValid,
      dispatchCreateOrderAction,
      dispatch,
      createOrderRequest,
    };

    onClickContinuePayment(paymentObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickContinuePayment]);

  const phonepeSwitchPaymentFailureHandler = useCallback(() => {
    dispatch(resetMiniAppPaymentReducer());
    redirectToPaymentFailurePage(checkoutProps);
  }, [checkoutProps, dispatch]);

  useEffect(() => {
    if (isPaymentCompleted && phonepeStatusResponse?.status) {
      switch (phonepeStatusResponse?.status) {
        case MINI_APP_CONSTANTS.FAILURE:
          phonepeSwitchPaymentFailureHandler();
          break;
        case MINI_APP_CONSTANTS.PENDING:
        case MINI_APP_CONSTANTS.SUCCESS:
        default:
          dispatch(resetMiniAppPaymentReducer());
          redirectToPaymentSuccessPage(checkoutProps);
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentCompleted, phonepeStatusResponse]);

  const phonepeCB = useCallback(
    status => {
      if (status === PHONEPE_SWITCH_CONSTANTS.SUCCESS) {
        dispatch(
          checkPhonepeSwitchPaymentStatus({
            tenantId,
            customerId: cartData?.customer?.id,
            transactionId: microAppPaymentResponse?.transactionId,
            maxCallCount: paymentStatusMaxCallCount,
          }),
        );
      } else {
        phonepeSwitchPaymentFailureHandler();
      }
    },
    [
      PHONEPE_SWITCH_CONSTANTS.SUCCESS,
      cartData?.customer?.id,
      dispatch,
      microAppPaymentResponse?.transactionId,
      paymentStatusMaxCallCount,
      phonepeSwitchPaymentFailureHandler,
      tenantId,
    ],
  );

  useEffect(() => {
    if (isGpayPaymentValid && microAppPaymentResponse?.reservationId && ChannelManager.isPhonePe) {
      const fallbackURL = `${window.location.origin}${ROUTE_URL.CHECKOUT}`;
      payUsingPhonePeSDK(microAppPaymentResponse?.reservationId, fallbackURL, phonepeCB);
      setGpayPaymentValidity(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microAppPaymentResponse]);

  const initiatePhonepeSwitchPayments = useCallback(() => {
    dispatchPayUsingPhonepeSwitch(
      tenantId,
      orderReducer?.response?.order?.id,
      tender?.name,
      cartData,
      dispatch,
      setGpayPaymentValidity,
    );
    setIsClickContinuePayment(false);
  }, [cartData, dispatch, orderReducer?.response?.order, setIsClickContinuePayment, tenantId, tender?.name]);

  const failGooglePayment = useCallback(() => {
    if (ChannelManager.isGoogle) {
      setGpayPaymentValidity(false);
      dispatch(resetGoogleSpotPayments());
      ChannelManager.payment = null;
      ChannelManager.oloPaymentInitializer = null;
      setIsClickContinuePayment(false);
      redirectToPaymentFailurePage(checkoutProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, setIsClickContinuePayment]);

  const initiateGoogleSpotPayments = useCallback(() => {
    const payload = {
      tenantId: tenantId,
      body: {
        orderId: orderReducer?.response?.order?.id,
        tenderType: ChannelManager.channel,
      },
    };
    ChannelManager.orderId = orderReducer?.response?.order?.id;
    setGpayPaymentValidity(true);
    dispatch(googleSpotInitiatePayment(payload));
  }, [dispatch, orderReducer?.response?.order, tenantId]);

  const googlePaymentSuccess = useCallback(() => {
    if (ChannelManager.isGoogle) {
      dispatch(resetGoogleSpotPayments());
      redirectToPaymentSuccessPage(checkoutProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      ChannelManager.addFeedback(
        `(initiatePaymentStatus, initiatePaymentResponse): ${JSON.stringify({
          initiatePaymentStatus,
          initiatePaymentResponse,
        })}`,
      );
      if (initiatePaymentResponse && isGpayPaymentValid) {
        await ChannelManager.BindPayment(initiatePaymentResponse);
        const payment = ChannelManager.payment;
        ChannelManager.addFeedback('Payment isSuccess data: ' + payment);

        if (payment?.code === 1) {
          setIsClickContinuePayment(false);
        } else if (payment?.code === 0 || (payment?.code && payment?.code !== 1)) {
          failGooglePayment();
        } else if (payment) {
          const validateGooglePayResponsePayload = {
            tenantId: tenantId,
            body: payment,
          };
          dispatch(googleSpotValidateResponse(validateGooglePayResponsePayload));
        }
        setGpayPaymentValidity(false);
      } else if (initiatePaymentStatus === MINI_APP_CONSTANTS.FAILURE) {
        failGooglePayment();
      }
    })();
  }, [
    dispatch,
    failGooglePayment,
    initiatePaymentResponse,
    initiatePaymentStatus,
    setIsClickContinuePayment,
    tenantId,
    isGpayPaymentValid,
  ]);

  useEffect(() => {
    ChannelManager.addFeedback(
      `(validatePaymentStatus, validatePaymentResponse): ${JSON.stringify({
        validatePaymentStatus,
        validatePaymentResponse,
      })}`,
    );

    if (validatePaymentResponse) {
      if (validatePaymentResponse?.status === MINI_APP_CONSTANTS.SUCCESS) {
        googlePaymentSuccess();
      } else {
        failGooglePayment();
      }
    } else if (validatePaymentStatus === MINI_APP_CONSTANTS.PENDING && ChannelManager.transactionId) {
      const transactionStatusCheckPayload = {
        tenantId: tenantId,
        transactionId: ChannelManager.transactionId,
      };
      dispatch(googleSpotCheckTransactionStatus(transactionStatusCheckPayload));
    }
  }, [dispatch, failGooglePayment, googlePaymentSuccess, tenantId, validatePaymentResponse, validatePaymentStatus]);

  useEffect(() => {
    ChannelManager.addFeedback(
      `(transactionStatus, transactionStatusResponse): ${JSON.stringify({
        transactionStatus,
        transactionStatusResponse,
      })}`,
    );
    if (transactionStatusResponse) {
      if (transactionStatusResponse?.status === MINI_APP_CONSTANTS.SUCCESS) {
        ChannelManager.addFeedback(
          `Great, channel manager payment status is NOT fail... ${transactionStatusResponse?.status}`,
        );
        googlePaymentSuccess();
      } else {
        ChannelManager.addFeedback(
          `Hmmm, channel manager payment status IS FAIL... ${transactionStatusResponse?.status}`,
        );
        failGooglePayment();
      }
    } else if (transactionStatus === MINI_APP_CONSTANTS.FAILURE) {
      if (ChannelManager.shouldGoogleSpotIgnoreOloFailure) {
        googlePaymentSuccess();
      } else {
        failGooglePayment();
      }
    }
  }, [dispatch, failGooglePayment, googlePaymentSuccess, transactionStatus, transactionStatusResponse]);

  useEffect(() => {
    const orderId = orderReducer?.response?.order?.id;
    if (isClickContinuePayment && orderId) {
      localStorage.setItem('postPaymentType', tender?.name);
      localStorage.setItem('paymentStatus', '');
      const paymentProvider = componentOptions?.payment?.paymentProvider;
      switch (tender?.name) {
        case PAYMENT_TENDER_TYPES.CREDIT_CARD:
        case PAYMENT_TENDER_TYPES.NET_BANKING:
        case PAYMENT_TENDER_TYPES.OTHER_WALLET:
        case PAYMENT_TENDER_TYPES.UPI:
          dispatchPayUsingNetBankings({ dispatch, tenantId, orderId, tender, formPayload, paymentProvider });
          break;
        case PAYMENT_TENDER_TYPES.PAY_BY_CASH:
          redirectToPaymentSuccessPage(checkoutProps);
          break;
        case PAYMENT_TENDER_TYPES.PAYTM:
          dispatchPayUsingPaytm(dispatch, tenantId, orderId, tender);
          break;
        case PAYMENT_TENDER_TYPES.GIFT_CARD:
          dispatchGiftCardAction(dispatch, tenantId, orderId, tender, formPayload, profileReducer);
          break;
        case PAYMENT_TENDER_TYPES.PHONEPE:
          dispatchPayUsingPhonePe(dispatch, tenantId, orderId, tender);
          break;
        case PAYMENT_TENDER_TYPES.GOOGLE_PAY_SPOT:
          initiateGoogleSpotPayments();
          break;
        case PAYMENT_TENDER_TYPES.PHONEPE_SWITCH:
          initiatePhonepeSwitchPayments();
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderReducer?.response?.order]);

  useEffect(() => {
    if (isClickContinuePayment && paymentsReducer && paymentsReducer?.response) {
      switch (tender?.name) {
        case PAYMENT_TENDER_TYPES.CREDIT_CARD:
          setFormPayload({
            ...formPayload,
            paymentRequestMessage: paymentsReducer?.response?.requestMessage,
          });
          break;
        case PAYMENT_TENDER_TYPES.NET_BANKING:
        case PAYMENT_TENDER_TYPES.OTHER_WALLET:
        case PAYMENT_TENDER_TYPES.UPI:
          setFormPayload({
            ...formPayload,
            requestParameters: paymentsReducer?.response?.requestParameters,
            postUrl: paymentsReducer?.response?.postUrl,
          });
          break;
        case PAYMENT_TENDER_TYPES.PAYTM:
          if (paymentsReducer?.response?.merchantId && paymentsReducer?.response?.merchantId !== '') {
            setFormPayload({
              ...formPayload,
              ...paymentsReducer?.response,
            });
          } else {
            callBackPaymentErrorHandler({ errorCode: 'payTmMerchantIdError' });
          }
          break;
        case PAYMENT_TENDER_TYPES.GIFT_CARD:
          if (paymentsGiftCardReducer.giftCardTransactionStatus === true) {
            redirectToPaymentSuccessPage(checkoutProps);
          } else if (
            paymentsGiftCardReducer.giftCardTransactionStatus === false &&
            paymentsGiftCardReducer.giftCardTransactionStatus === undefined
          ) {
            //Redirect to failure page
            redirectToPaymentFailurePage(checkoutProps);
          }
          break;

        case PAYMENT_TENDER_TYPES.PHONEPE:
          setFormPayload({
            ...formPayload,
            ...paymentsReducer?.response,
            tenderType: tender?.name,
          });
          break;
        default:
          break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsReducer?.response, paymentsGiftCardReducer]);

  return (
    <>
      {isClickContinuePayment && renderPaymentForms({ tender, formPayload, orderReducer, paymentsReducer })}

      {isClickContinuePayment &&
        (tender?.name === PAYMENT_TENDER_TYPES.OTHER_WALLET || tender?.name === PAYMENT_TENDER_TYPES.UPI) && (
          <PaymentFormNetBankingOtherWalletUPIBilldesk {...formPayload} />
        )}

      {isClickContinuePayment && tender?.name === PAYMENT_TENDER_TYPES.PAYTM && <PaymentFormPayTm {...formPayload} />}

      {isClickContinuePayment && tender?.name === PAYMENT_TENDER_TYPES.PHONEPE && (
        <PaymentFormPhonePe {...formPayload} />
      )}

      {ChannelManager.isBrowser && (
        <div>
          <div className={`row row-cols-12 mt-10px`}>
            <div className={`labelContent contact-label pl-25px col-sm-4 col-4`}>
              <h2 aria-label={translateWithI18Next(CHECKOUT_TITLES.PAYMENT)}>
                {translateWithI18Next(CHECKOUT_TITLES.PAYMENT)}
              </h2>
            </div>

            {tender?.name === '' && (
              <div className={`addPaymentContent px-25px col-sm-8 col-8`}>
                <div className='d-flex flex-column mt-20px'>
                  <ContainedButton
                    variant={ButtonType.primary}
                    fullWidth
                    data-testid='pay-button'
                    className='payButton payButton-in'
                    onClick={() =>
                      addToPaymentClickHandler(
                        paymentEnabled,
                        isClickAddPayment,
                        setIsClickAddPayment,
                        setFormPayload,
                        userEmail,
                        userPhone,
                        setTender,
                        setIsPaymentTypeSelected,
                        setIsClickContinuePayment,
                        callBackValidateUserDetails,
                      )
                    }
                  >
                    {translateWithI18Next(ADD_PAY_METHOD)}
                  </ContainedButton>
                </div>
              </div>
            )}

            {/* TENDER BUTTON(NEEDS CONDITION RENDER ) */}
            {tender?.name !== '' && (
              <div className={`addPaymentContent col-sm-12 col-md-8 selectedPayment`}>
                <div className='d-flex flex-column mt-20px'>
                  <ButtonComp
                    className='tenderButton'
                    data-testid='tender-btn'
                    onClick={() =>
                      addToPaymentClickHandler(
                        paymentEnabled,
                        isClickAddPayment,
                        setIsClickAddPayment,
                        setFormPayload,
                        userEmail,
                        userPhone,
                        setTender,
                        setIsPaymentTypeSelected,
                        setIsClickContinuePayment,
                        callBackValidateUserDetails,
                      )
                    }
                  >
                    {tender && tender?.name && tender?.payload && tender?.payload?.url && (
                      <span className='payment-image'>
                        <img src={tender?.payload?.url} alt='TenderImage' />
                      </span>
                    )}
                    {tender?.name === PAYMENT_TENDER_TYPES.CREDIT_CARD && (
                      <div className='card-number-image'>
                        <svg {...formPayload?.svgSource} />
                      </div>
                    )}
                    <span className='col-12 col-xs-10'>
                      {tender?.name === PAYMENT_TENDER_TYPES.CREDIT_CARD &&
                        'x - ' + formPayload?.cardNumber?.trim()?.substr(formPayload?.cardNumber.trim()?.length - 4)}

                      {(tender?.name === PAYMENT_TENDER_TYPES.NET_BANKING ||
                        tender?.name === PAYMENT_TENDER_TYPES.OTHER_WALLET) &&
                        tender?.payload?.displayName + ' : ' + formPayload?.bankName}

                      {tender?.name === PAYMENT_TENDER_TYPES.GIFT_CARD &&
                        tender?.payload?.displayName + ': #' + formPayload?.AdditionalAttributes?.GiftCardNumber}

                      {(tender?.name === PAYMENT_TENDER_TYPES.PAYTM ||
                        tender?.name === PAYMENT_TENDER_TYPES.PHONEPE ||
                        tender?.name === PAYMENT_TENDER_TYPES.PAY_BY_CASH ||
                        tender?.name === PAYMENT_TENDER_TYPES.UPI) &&
                        tender?.payload?.displayName}
                    </span>

                    <span className='right-arrow-icon'>
                      <img src={GoIcon} alt='' />
                    </span>
                  </ButtonComp>
                  {tender?.name === PAYMENT_TENDER_TYPES.PAYTM && (
                    <span className='paytmPaymentProcess mt-10px'> * Payment will process after place your order.</span>
                  )}
                </div>
              </div>
            )}
            {/*  */}
            <ProceedToPayment
              isClickAddPayment={isClickAddPayment}
              setIsClickAddPayment={setIsClickAddPayment}
              cartData={cartData}
              isValidated={isValidated}
              basketId={basketId}
              tenantId={tenantId}
              tender={tender}
              setTender={setTender}
              isValid={isValid}
              checkoutProps={checkoutProps}
              setIsValid={setIsValid}
              formPayload={formPayload}
              setFormPayload={setFormPayload}
              setIsPaymentTypeSelected={setIsPaymentTypeSelected}
              currencyConversionFactor={currencyConversionFactor}
            />
          </div>
        </div>
      )}
    </>
  );
}
